/**
 * Currency-decimal registry for R22-3 (Phase 22 — Currency Precision Hardening).
 *
 * Mirrors the `currencyDecimals` field added to
 * `@omnitronix/game-shell-contract@0.6.0` ShellConfig. Kept inline here rather
 * than consumed from the contract because this frontend does not currently
 * integrate with the game shell (it receives launch params via URL) and the
 * contract package is not yet published to npm.
 *
 * - Fiat ISO-4217 defaults to 2 (cents / pence).
 * - Crypto codes follow their canonical minor-unit precision.
 *
 * If a currency is unknown, defaults to 2 decimals (safe fiat fallback).
 */
export const CURRENCY_DECIMALS: Readonly<Record<string, number>> = {
  // Fiat (ISO-4217) — 2 decimals
  EUR: 2,
  USD: 2,
  GBP: 2,
  CAD: 2,
  AUD: 2,
  NZD: 2,
  CHF: 2,
  SEK: 2,
  NOK: 2,
  DKK: 2,
  PLN: 2,
  // Zero-decimal fiat
  JPY: 0,
  KRW: 0,
  // Crypto
  BTC: 8,
  ETH: 18,
  USDT: 6,
  USDC: 6,
  DOGE: 8,
  XRP: 6,
  TRX: 6,
  BNB: 18,
  SOL: 9,
  LTC: 8,
} as const;

/**
 * Resolve the fraction-digit precision for a currency code.
 *
 * @param currency - ISO-4217 fiat code or crypto symbol (case-insensitive).
 * @returns number of fraction digits (falls back to 2 for unknown codes).
 */
export function getCurrencyDecimals(currency: string | null | undefined): number {
  if (!currency) return 2;
  const decimals = CURRENCY_DECIMALS[currency.toUpperCase()];
  return decimals ?? 2;
}
